'use client'
import type { FC, ReactNode } from 'react'
import { createContext, useCallback, useMemo, useState } from 'react'

export enum MODALS {
  ADD_TO_CART = 'dialog--add_to_cart',
  AUTH = 'dialog--auth',
  CART_CONTROLS = 'dialog--cart_controls',
  CART_TYPE = 'dialog--cart_type',
  COOKIES = 'dialog--cookies',
  COMERCIAL = 'dialog--comercial_region',
  CONFIRM_DELETE_ADDRESS = 'dialog--confirm_delete_address',
  FR_NO_CART = 'dialog--fr_no_cart',
  FILTERS = 'dialog--filters',
  MENU = 'dialog--menu',
  MENU_ACCOUNT = 'dialog--account',
  NEWSLETTER = 'dialog--newsletter',
  NOTIFY_ME = 'dialog--notify_me',
  PROMO = 'dialog--promo',
  PRODUCT = 'dialog--product',
  PUBLIC_INTERESTED = 'dialog--public_interested',
  REGION = 'dialog--region',
  REGION_STORE = 'dialog--region_store',
  SEARCH = 'dialog--search',
  STOCK_CART = 'dialog--stock_cart',
  VIDEO = 'dialog--video',
  LOADING_SCREEN = 'dialog--loading_screen',
  POSTAL_CODE = 'dialog--postal_code',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface iDefaultModal { isActive: boolean, payload?: any }

interface iModalAuthPayload {
  payload?: {
    level: string
  }
}

type TypeModalException = {
  [MODALS.AUTH]: iModalAuthPayload
}

// Utility to merge two Definitions: B object properties shadowing -> A object properties
type $Merge<TA, TB> = Omit<TA, keyof TB> & TB;
// Getting the Base+Extended type of an Modal
type TypeModalById<A extends MODALS> = A extends keyof TypeModalException ? $Merge<iDefaultModal, TypeModalException[A]> : iDefaultModal


type TypeModals = {
  [key in MODALS]: TypeModalById<key>
}
type TypeModalsState = {
  modals: TypeModals,
  setModal: (modal: { [key: string]: iDefaultModal } | ((prev: TypeModals) => TypeModals)) => void,
  someModalIsActive: boolean
}

const getInitialModalsState = () => {
	return Object.values(MODALS).reduce((acc, modal) => {
		// PENDING A WAY TO INITIALIZE Modal Exceptions
		acc[modal] = { isActive: false }

		return acc
	}, {} as TypeModals)
}

const initialState: TypeModalsState = {
  modals: getInitialModalsState(),
  setModal: () => { },
  someModalIsActive: false
}

interface iModalsProviderProps {
  children: ReactNode
}

export const ModalsContext = createContext<TypeModalsState | null>(initialState)

export const ModalsProvider: FC<iModalsProviderProps> = ({ children }) => {
  const [modals, setModals] = useState<TypeModals>(initialState.modals)
  const [someModalIsActive, setSomeModalIsActive] = useState(initialState.someModalIsActive)

  const setModal = useCallback((modal: { [key: string]: iDefaultModal } | ((prev: TypeModals) => TypeModals)) => {
    setModals(prevModals => {
      const nextModals = typeof modal === 'function' ? modal(prevModals) : { ...prevModals, ...modal }
      const someActive = Object.values(nextModals).some(m => m?.isActive)
      setSomeModalIsActive(someActive)
      return nextModals
    })
  }, [])

  const contextValue = useMemo(() => ({ modals, setModal, someModalIsActive }), [modals, setModal, someModalIsActive])

  return (<ModalsContext.Provider value={contextValue}>
    {children}
  </ModalsContext.Provider>)
}
